import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { formatQuantity, quantityStep } from "../utils/quantity";

const MOVEMENT_TYPES = [
  { value: "purchase", label: "Purchase / Restock (+)" },
  { value: "return", label: "Customer Return (+)" },
  { value: "adjustment", label: "Adjustment (+/-)" },
  { value: "damage", label: "Damage (-)" },
  { value: "write_off", label: "Write-off (-)" },
];

const EMPTY_FORM = { movement_type: "purchase", quantity_delta: "", unit_cost: "", reason: "" };

export default function StockMovementPanel({ shopId, product, canAdjust, onClose, onChanged }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  // Local copy so the displayed quantity updates immediately from the POST
  // response, rather than staying stuck at the snapshot the panel opened
  // with until the parent's product list happens to re-render.
  const [currentQuantity, setCurrentQuantity] = useState(product.quantity_on_hand);

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/shops/${shopId}/products/${product.id}/stock-movements`);
      setMovements(res.data);
    } catch (err) {
      setError("Failed to load stock history");
    }
    setLoading(false);
  }, [shopId, product.id]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const magnitude = Math.abs(Number(form.quantity_delta));
      const isNegativeType = form.movement_type === "damage" || form.movement_type === "write_off";
      const quantity_delta =
        form.movement_type === "adjustment"
          ? Number(form.quantity_delta)
          : isNegativeType
          ? -magnitude
          : magnitude;

      const res = await api.post(`/shops/${shopId}/products/${product.id}/stock-movements`, {
        movement_type: form.movement_type,
        quantity_delta,
        unit_cost: form.unit_cost ? Number(form.unit_cost) : null,
        reason: form.reason || null,
      });
      setCurrentQuantity(res.data.quantity_on_hand);
      setForm(EMPTY_FORM);
      await fetchMovements();
      onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to record movement");
    }
    setSubmitting(false);
  };

  return (
    <div className="card stock-panel">
      <div className="stock-panel-header">
        <h2>Stock: {product.name}</h2>
        <button className="btn btn-secondary" onClick={onClose} type="button">
          Close
        </button>
      </div>
      <div className="chip">Current quantity: {formatQuantity(currentQuantity, product.unit_of_measure)}</div>

      {canAdjust && (
        <>
          <form onSubmit={handleSubmit} className="stock-form">
            <select name="movement_type" value={form.movement_type} onChange={handleChange}>
              {MOVEMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              type="number"
              name="quantity_delta"
              placeholder={form.movement_type === "adjustment" ? "Quantity (+/-)" : "Quantity"}
              value={form.quantity_delta}
              onChange={handleChange}
              step={quantityStep(product.unit_of_measure)}
              required
            />
            <input
              type="number"
              name="unit_cost"
              placeholder="Unit cost (optional)"
              value={form.unit_cost}
              onChange={handleChange}
              step="0.01"
            />
            <input
              type="text"
              name="reason"
              placeholder="Reason (optional)"
              value={form.reason}
              onChange={handleChange}
            />
            <button className="btn" type="submit" disabled={submitting}>
              Record
            </button>
          </form>
          {error && <div className="error-msg">{error}</div>}
        </>
      )}

      <h3 className="stock-history-title">History</h3>
      {loading ? (
        <div className="loader">Loading...</div>
      ) : (
        <div className="scroll-x">
          <table className="product-table stock-history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Change</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((m) => (
                <tr key={m.id}>
                  <td>{new Date(m.created_at).toLocaleString()}</td>
                  <td>{m.movement_type}</td>
                  <td className={m.quantity_delta >= 0 ? "delta-positive" : "delta-negative"}>
                    {m.quantity_delta > 0 ? "+" : m.quantity_delta < 0 ? "-" : ""}
                    {formatQuantity(Math.abs(m.quantity_delta), product.unit_of_measure)}
                  </td>
                  <td>{m.reason || "-"}</td>
                </tr>
              ))}
              {movements.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    No stock movements yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
